/**
 * Visualization instances. A layer owns one instance; the instance knows how to draw its
 * visualization kind (native GLSL now; ISF multi-pass and Butterchurn plug in here too).
 */
import { MAX_VIZ_PARAMS } from '../../../shared/params';
import {
  FULLSCREEN_VS,
  ShaderError,
  createProgram,
  finishProgram,
  isProgramReady,
  uniform,
  type GL,
  type Program,
  type RenderTarget,
} from '../gl/gl';
import { setCommonUniforms, type FrameContext } from '../frame';
import { PRELUDE } from '../visualizations/prelude';
import type { VisualizationDef } from '../visualizations';

export interface PaletteBinding {
  tex: WebGLTexture;
  shift: number;
  cyclic: boolean;
}

export interface VizInstance {
  /** Compiled and able to draw. */
  readonly ready: boolean;
  /** Failed permanently (compile error); the layer is skipped. */
  readonly failed: boolean;
  /** Why it failed, for the user. */
  readonly errorMessage?: string | null;
  render(
    fc: FrameContext,
    params: Float32Array,
    palette: PaletteBinding,
    target: RenderTarget,
  ): void;
  dispose(): void;
}

type Parallel = { COMPLETION_STATUS_KHR: number } | null;

/**
 * Programs are shared between instances (two layers or two scenes using the same visualization
 * compile once). Compilation uses KHR_parallel_shader_compile when available, so a new
 * visualization never stalls the frame loop.
 */
export class ProgramCache {
  private entries = new Map<
    string,
    { pending: WebGLProgram | null; program: Program | null; error: string | null }
  >();

  constructor(
    private readonly gl: GL,
    private readonly parallel: Parallel,
    private readonly onError: (key: string, message: string) => void,
  ) {}

  get(key: string, fragment: () => string): { program: Program | null; error: string | null } {
    let e = this.entries.get(key);
    if (!e) {
      let pending: WebGLProgram | null = null;
      let error: string | null = null;
      try {
        pending = createProgram(this.gl, FULLSCREEN_VS, fragment());
      } catch (err) {
        error = String(err);
      }
      e = { pending, program: null, error };
      this.entries.set(key, e);
    }
    if (e.pending && isProgramReady(this.gl, e.pending, this.parallel)) {
      try {
        e.program = finishProgram(this.gl, e.pending);
      } catch (err) {
        e.error = err instanceof ShaderError ? err.message : String(err);
        this.onError(key, e.error);
      }
      e.pending = null;
    }
    return e;
  }

  forget(key: string): void {
    const e = this.entries.get(key);
    if (e?.program) this.gl.deleteProgram(e.program.program);
    this.entries.delete(key);
  }

  clear(): void {
    this.entries.clear();
  }
}

/** A single-pass GLSL visualization written against PRELUDE. */
export class NativeViz implements VizInstance {
  private program: Program | null = null;
  private error: string | null = null;

  constructor(
    private readonly cache: ProgramCache,
    private readonly def: VisualizationDef,
  ) {
    this.poll();
  }

  private poll(): void {
    if (this.program || this.error) return;
    const e = this.cache.get(`native:${this.def.id}`, () => PRELUDE + this.def.fragment);
    this.program = e.program;
    this.error = e.error;
  }

  get ready(): boolean {
    this.poll();
    return this.program !== null;
  }

  get failed(): boolean {
    return this.error !== null;
  }

  render(
    fc: FrameContext,
    params: Float32Array,
    palette: PaletteBinding,
    target: RenderTarget,
  ): void {
    const p = this.program;
    if (!p) return;
    const gl = fc.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
    gl.viewport(0, 0, target.width, target.height);
    gl.useProgram(p.program);
    setCommonUniforms(fc, p, target.width, target.height, palette);
    gl.uniform1fv(
      uniform(gl, p, 'uP'),
      params.length === MAX_VIZ_PARAMS ? params : padParams(params),
    );
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  dispose(): void {
    // Programs are owned by the cache.
  }
}

const padded = new Float32Array(MAX_VIZ_PARAMS);
function padParams(params: Float32Array): Float32Array {
  padded.fill(0);
  padded.set(params.subarray(0, MAX_VIZ_PARAMS));
  return padded;
}
